import { useEffect, useState } from 'react';
import type { ReactNode } from 'react';
import { Link } from 'react-router-dom';
import { ApiError, api, serverInUseDetails } from '../api';
import { environmentKindLabel, labelFor, serverDependencyReasonLabel, statusFor } from '../labels';
import type { StatusKind } from '../labels';
import type { AtomicPlanStep, DeploymentPlanStep, Identity, Organization } from '../api';
import { useTheme } from '../hooks';
import type { ThemePreference } from '../hooks';
import { Dialog } from './primitives/Dialog';
import { useMenu } from './primitives/useMenu';
import { Callout } from './primitives/Callout';
import { StepList } from './primitives/Steps';
import { Field, Input } from './primitives';

/** Skeleton is one placeholder block shaped like the content that will
 * replace it — a plain rectangle sized by the caller, so a page's skeleton
 * can echo its real layout (a title-width bar, a row of card-height
 * blocks) instead of a single generic spinner. `aria-hidden`: the loading
 * container around it carries the accessible status. */
export function Skeleton({
  width,
  height = '1em',
  radius = 'var(--radius-sm)',
  className,
}: {
  width: string;
  height?: string;
  radius?: string;
  className?: string;
}) {
  return (
    <span
      className={`skeleton${className ? ` ${className}` : ''}`}
      style={{ width, height, borderRadius: radius }}
      aria-hidden="true"
    />
  );
}

/** SkeletonPage renders a page-shaped set of skeleton blocks: a heading, an
 * optional row of stat-card blocks, then `rows` list-row blocks — the
 * shape every list/detail page in this app converges on. Pass this as the
 * loading state instead of loading text wherever the eventual content has
 * that shape, so the layout does not jump when real data arrives. */
export function SkeletonPage({ cards = 0, rows = 3 }: { cards?: number; rows?: number }) {
  return (
    <div className="skeleton-page" role="status" aria-label="Loading">
      <Skeleton width="240px" height="28px" className="skeleton-page__title" />
      {cards > 0 ? (
        <div className="skeleton-page__cards">
          {Array.from({ length: cards }).map((_, i) => (
            <Skeleton key={i} width="100%" height="88px" radius="var(--radius-md)" />
          ))}
        </div>
      ) : null}
      <div className="skeleton-page__rows">
        {Array.from({ length: rows }).map((_, i) => (
          <Skeleton key={i} width="100%" height="56px" radius="var(--radius-md)" />
        ))}
      </div>
    </div>
  );
}

/** AuthCardSkeleton is SkeletonPage's counterpart for the narrow
 * .auth-card context (sign-in, accept-invitation, verify-email): a
 * heading-shaped bar and a couple of field-shaped rows sized to that
 * card, rather than SkeletonPage's full-width dashboard shape. */
export function AuthCardSkeleton() {
  return (
    <div className="skeleton-page skeleton-page--auth-card" role="status" aria-label="Loading">
      <Skeleton width="70%" height="24px" />
      <Skeleton width="100%" height="16px" />
      <Skeleton width="85%" height="16px" />
      <Skeleton width="100%" height="40px" radius="var(--radius-sm)" />
    </div>
  );
}

/** LoadingRows is Loading's counterpart for a sub-section within an
 * already-rendered card (a form, a list, a table) rather than a whole
 * page: a single text line collapsing into several rows of real content
 * reads as a layout jump even inside an already-mounted card.
 * `rowHeight` should roughly match what is actually about to render
 * there (an input row, a list item, a table row). */
export function LoadingRows({ label, rows = 3, rowHeight = '20px' }: { label: string; rows?: number; rowHeight?: string }) {
  return (
    <div className="loading-rows" role="status" aria-label={label}>
      {Array.from({ length: rows }).map((_, i) => (
        <Skeleton key={i} width="100%" height={rowHeight} />
      ))}
    </div>
  );
}

/** errorTitles turns the API's stable error codes into a sentence a person
 * can act on. The code itself is still shown, small, because support and
 * the audit log refer to it — but it is never the headline. Codes not
 * listed fall back to a generic title; the API message stays as the
 * detail. */
const errorTitles: Record<string, string> = {
  not_authenticated: 'You are signed out',
  session_invalid: 'Your session has expired',
  unauthorized: 'You do not have permission to do this',
  forbidden: 'You do not have permission to do this',
  invalid_credentials: 'The email or password is not right',
  not_found: 'Not found',
  network_error: 'Commerce Ops is not reachable',
  api_unavailable: 'Commerce Ops is not reachable',
  bad_response: 'Commerce Ops sent an answer it could not read',
  monitor_not_found: 'That monitor no longer exists',
  incident_not_found: 'That incident no longer exists',
  conflict: 'Something changed in the meantime',
  environment_busy: 'This environment is busy',
  illegal_transition: 'That step is not allowed from the current state',
  validation_error: 'Please check the highlighted field',
  validation_failed: 'Please check the highlighted field',
  invalid_input: 'Please check the highlighted field',
  invalid_json: 'The request could not be read',
  invalid_body: 'The request could not be read',
  internal_error: 'Commerce Ops ran into an unexpected problem',
  object_storage_unavailable: 'Backup storage is not configured for this Commerce Ops installation',
  backup_storage_not_configured: 'Backup storage is not configured for this Commerce Ops installation',
  hetzner_not_configured: 'Hetzner provisioning is not configured for this Commerce Ops installation',
  billing_not_configured: 'Billing is not configured for this Commerce Ops installation',
  email_not_configured: 'Email delivery is not configured for this Commerce Ops installation',
  server_connection_unavailable: 'Commerce Ops could not reach the server over SSH',
  atomic_template_not_configured: 'The deployment paths for this environment are not set yet',
  magento_backup_requires_atomic_config: 'The deployment paths for this environment are not set yet',
  repository_url_not_configured: 'The project has no repository URL yet',
  repository_access_not_configured: 'Repository access is not configured for this project',
  git_credential_rejected: 'The Git provider rejected the stored token',
  repository_not_accessible: 'The repository does not exist or the token cannot read it',
  branch_not_found: 'The repository has no branch with that name',
  git_provider_rate_limited: 'The Git provider is rate-limiting requests right now',
  git_provider_unreachable: 'Commerce Ops could not reach the Git provider',
  git_provider_unexpected_response: 'The Git provider answered in an unexpected way',
  server_in_use: 'This server is still used by environments',
  unsupported_repository_url: 'That repository URL is not supported',
  live_execution_confirmation_mismatch: 'The environment name you typed does not match',
  live_execution_requires_verified_connections: 'Both connections must verify successfully first',
  live_execution_requires_configured_mode: 'The server connection is not configured yet',
  organization_has_active_managed_server: 'This organization already has a managed server',
  belongs_to_another_organization: 'That record belongs to another organization',
  already_member: 'That person is already a member',
  cannot_invite_owner: 'An owner cannot be invited again',
  invitation_not_acceptable: 'This invitation can no longer be accepted',
  token_invalid: 'This link is no longer valid',
  secret_value_required: 'A secret value is required',
  secret_not_resolvable: 'A referenced secret could not be read',
  simulated_result_rejected: 'The agent returned a simulated result, which Commerce Ops refuses',
  rollback_unsafe: 'Rolling back is not safe for this deployment',
  sync_source_missing: 'Choose a source environment or backup first',
  grant_required: 'Approval is required before this runs',
  grant_invalid: 'The approval is no longer valid',
  restore_verify_wrong_source: 'This backup cannot be verified against that source',
  stripe_event_in_flight: 'Billing is still processing a previous event',
};

/**
 * ErrorNotice says what failed, with the server's own words, and offers the
 * next step when there is one: `onRetry` runs the same read again;
 * `nextStep` says what else to do. It never invents a cause.
 */
export function ErrorNotice({ error, onRetry, nextStep }: { error: Error | null; onRetry?: () => void; nextStep?: ReactNode }) {
  if (!error) return null;

  const isApiError = error instanceof ApiError;
  const field = isApiError ? error.field : undefined;
  const code = isApiError ? error.code : undefined;
  const title = (code && errorTitles[code]) ?? (isApiError ? 'Commerce Ops could not complete that' : 'This could not be completed');
  const showMessage = error.message && error.message !== title;

  return (
    <Callout
      tone="danger"
      title={title}
      className="error-notice"
      actions={
        onRetry ? (
          <button type="button" className="button button--secondary button--small" onClick={onRetry}>
            Retry
          </button>
        ) : undefined
      }
    >
      {showMessage ? <p>{error.message}</p> : null}
      {nextStep ? <p>{nextStep}</p> : null}
      {field || code ? (
        <p className="error-notice__meta">
          {field ? `Field: ${field}` : null}
          {field && code ? ' · ' : null}
          {code ? (
            <>
              Code: <code>{code}</code>
            </>
          ) : null}
        </p>
      ) : null}
    </Callout>
  );
}

/** ServerInUseNotice explains a refused server removal: the environments
 * that still use the server, each linking to its project so the person can
 * move or remove it. Renders nothing for any other error. */
export function ServerInUseNotice({ error }: { error: Error | null }) {
  const details = serverInUseDetails(error);
  if (!details) return null;
  return (
    <Callout tone="danger" title="This server is still used by the following environments." className="server-in-use">
      <p>
        Move each environment to another server or remove it, then remove {details.server_name || 'the server'} again.
        Nothing was changed.
      </p>
      <ul className="server-in-use__list">
        {details.environments.map((env) => (
          <li key={env.environment_id}>
            <Link to={`/app/projects/${env.project_id}`}>
              {env.project_name} — {env.environment_name}
            </Link>
            <span className="muted">
              {' '}
              ({labelFor(environmentKindLabel, env.environment_kind).toLowerCase()};{' '}
              {env.reasons.map((r) => labelFor(serverDependencyReasonLabel, r).toLowerCase()).join(', ')})
            </span>
          </li>
        ))}
      </ul>
    </Callout>
  );
}

export function EmptyState({
  title,
  description,
  children,
}: {
  title: string;
  /** Only when the empty state needs a sentence to be left (a required action); a title and a button usually say enough. */
  description?: string;
  children?: ReactNode;
}) {
  return (
    <div className="empty-state">
      <h3>{title}</h3>
      {description ? <p className="muted">{description}</p> : null}
      {children}
    </div>
  );
}

/**
 * StatusBadge is the one way a status is shown: a pill with a
 * sentence-case label, coloured only by its tone. Words and tone come from
 * labels.ts statusFor(kind, value) — never from the page — so "failed",
 * "cancelled" or "not checked yet" look the same everywhere. `label`
 * overrides the words when the context needs a more specific sentence; the
 * tone still comes from the status.
 */
export function StatusBadge({
  kind,
  value,
  label,
  title,
  className,
  testId,
  variant = 'soft',
}: {
  kind: StatusKind;
  value: string | null | undefined;
  label?: ReactNode;
  title?: string;
  className?: string;
  testId?: string;
  /** "soft": a tinted pill, for a state worth recognising at a glance;
   * "plain": the words only, tone-coloured with no fill, for history and
   * secondary facts (CO-150) — a column of identical pills is noise. */
  variant?: 'soft' | 'plain';
}) {
  const status = statusFor(kind, value);
  return (
    <span className={`badge badge--${status.tone}${variant === 'plain' ? ' badge--plain' : ''}${className ? ` ${className}` : ''}`} title={title} data-status={`${kind}:${status.value}`} data-testid={testId}>
      {label ?? status.label}
    </span>
  );
}

/**
 * PlanStepList renders the ordered, immutable steps of an atomic template —
 * either a live, unpersisted preview (AtomicPlanStep) or the persisted,
 * immutable snapshot one deployment attempt started with (DeploymentPlanStep,
 * which additionally carries a durable per-step state).
 */
export function PlanStepList({ steps }: { steps: (AtomicPlanStep | DeploymentPlanStep)[] }) {
  return (
    <StepList
      label="Plan steps"
      steps={steps.map((step) => ({
        key: String(step.sequence),
        title: step.label,
        meta: <StatusBadge kind="safetyBoundary" value={step.safety_boundary} />,
        status: 'state' in step ? { kind: 'execution', value: step.state } : undefined,
      }))}
    />
  );
}

const themeCycle: Record<ThemePreference, ThemePreference> = {
  system: 'light',
  light: 'dark',
  dark: 'system',
};

const themeIcon: Record<ThemePreference, ReactNode> = {
  system: (
    <svg width="14" height="14" viewBox="0 0 16 16" fill="none" aria-hidden="true">
      <rect x="1.5" y="2.5" width="13" height="9" rx="1.5" stroke="currentColor" strokeWidth="1.3" />
      <path d="M5.5 14.5h5M8 11.5v3" stroke="currentColor" strokeWidth="1.3" strokeLinecap="round" />
    </svg>
  ),
  light: (
    <svg width="14" height="14" viewBox="0 0 16 16" fill="none" aria-hidden="true">
      <circle cx="8" cy="8" r="3.4" stroke="currentColor" strokeWidth="1.3" />
      <path
        d="M8 1.3v1.6M8 13.1v1.6M2.6 8H1M15 8h-1.6M3.5 3.5l1.1 1.1M11.4 11.4l1.1 1.1M12.5 3.5l-1.1 1.1M4.6 11.4l-1.1 1.1"
        stroke="currentColor"
        strokeWidth="1.3"
        strokeLinecap="round"
      />
    </svg>
  ),
  dark: (
    <svg width="14" height="14" viewBox="0 0 16 16" fill="none" aria-hidden="true">
      <path
        d="M13.8 9.5A6 6 0 116.5 2.2a5 5 0 007.3 7.3z"
        stroke="currentColor"
        strokeWidth="1.3"
        strokeLinejoin="round"
      />
    </svg>
  ),
};

const themeLabel: Record<ThemePreference, string> = {
  system: 'System theme',
  light: 'Light theme',
  dark: 'Dark theme',
};

/** ThemeToggle cycles system → light → dark → system. A real <button>, so it
 * is keyboard-reachable and operable (Enter/Space) with a visible focus
 * ring from styles.css's global :focus-visible rule, and its label always
 * names the current choice for screen readers rather than just an icon. */
export function ThemeToggle({ className, menuItem = false }: { className?: string; /** Inside a `role="menu"`: the toggle is then a menu item. */ menuItem?: boolean } = {}) {
  const [theme, setTheme] = useTheme();
  return (
    <button
      type="button"
      role={menuItem ? 'menuitem' : undefined}
      className={`theme-toggle${className ? ` ${className}` : ''}`}
      onClick={() => setTheme(themeCycle[theme])}
      aria-label={`${themeLabel[theme]}. Click to change.`}
      title={themeLabel[theme]}
    >
      {themeIcon[theme]}
      {themeLabel[theme]}
    </button>
  );
}

function initials(nameOrEmail: string): string {
  const trimmed = nameOrEmail.trim();
  if (!trimmed) return '?';
  const parts = trimmed.split(/\s+/);
  const first = parts[0] ?? '';
  const second = parts[1] ?? '';
  if (first && second) return (first.slice(0, 1) + second.slice(0, 1)).toUpperCase();
  return trimmed.slice(0, 2).toUpperCase();
}

/** ProfileMenu sits at the bottom of the sidebar: a single button naming
 * the signed-in person, opening a keyboard- and click-outside-dismissible
 * menu with theme, account destinations, and sign out. A real
 * `<button aria-haspopup aria-expanded>` and `role="menu"`/`role="menuitem"`
 * children, not a bare styled div. Its keyboard and pointer behaviour is
 * `useMenu`, the same as the "…" ActionMenu's. */
export function ProfileMenu({
  identity,
  organization,
  onSignOut,
  placement = 'down',
  compact = false,
}: {
  identity: Identity;
  organization: Organization;
  onSignOut: () => void;
  /** "up" at the bottom of the sidebar (CO-150). */
  placement?: 'up' | 'down';
  /** The icon rail: the avatar alone, named for assistive technology. */
  compact?: boolean;
}) {
  const [signingOut, setSigningOut] = useState(false);
  // Every control the panel offers, read live off the DOM: the list changes
  // with isSession and signingOut, and with the viewport (the narrow-screen
  // only items are display:none on desktop and must not swallow a keypress).
  const { open, setOpen, rootRef, triggerRef, panelRef, triggerProps } = useMenu({
    items: (panel) =>
      Array.from(panel.querySelectorAll<HTMLElement>('a, button:not(:disabled)')).filter((item) => window.getComputedStyle(item).display !== 'none'),
  });

  const name = identity.email ?? 'Local development';
  const isSession = identity.kind === 'session';

  async function handleSignOut() {
    setSigningOut(true);
    try {
      await api.logout();
    } finally {
      setSigningOut(false);
      setOpen(false);
      onSignOut();
    }
  }

  return (
    <div className={`profile-menu profile-menu--${placement}${compact ? ' profile-menu--compact' : ''}`} ref={rootRef}>
      <button
        ref={triggerRef}
        type="button"
        className="profile-menu__trigger"
        {...triggerProps}
        aria-label={compact ? `Account: ${name}` : undefined}
      >
        <span className="profile-menu__avatar" aria-hidden="true">
          {initials(name)}
        </span>
        {compact ? null : (
          <span className="profile-menu__trigger-text">
            <span className="profile-menu__trigger-name">{name}</span>
            <span className="profile-menu__trigger-org muted">{organization.name}</span>
          </span>
        )}
      </button>
      {open ? (
        <div className="profile-menu__panel" role="menu" aria-label="Account menu" ref={panelRef}>
          <div className="profile-menu__identity">
            <div className="profile-menu__name">{name}</div>
            <div className="profile-menu__org muted">{organization.name}</div>
            {isSession && identity.role ? (
              <span className="badge badge--neutral">{identity.role.name}</span>
            ) : null}
          </div>
          <div className="profile-menu__divider" role="separator" />
          {/* Team and Settings are in the sidebar (CO-150); this menu is the
              person's own: their account, billing, theme and signing out. */}
          {isSession ? (
            <>
              <Link className="profile-menu__item" role="menuitem" to="/app/settings?tab=account" onClick={() => setOpen(false)}>
                Account
              </Link>
              <Link className="profile-menu__item" role="menuitem" to="/app/settings?tab=billing" onClick={() => setOpen(false)}>
                Billing
              </Link>
              <div className="profile-menu__divider" role="separator" />
            </>
          ) : null}
          <div className="profile-menu__item profile-menu__item--static" role="none">
            <ThemeToggle menuItem />
          </div>
          {isSession ? (
            <button
              className="profile-menu__item profile-menu__item--danger"
              role="menuitem"
              type="button"
              onClick={handleSignOut}
              disabled={signingOut}
            >
              {signingOut ? 'Signing out' : 'Sign out'}
            </button>
          ) : null}
        </div>
      ) : null}
    </div>
  );
}

/** ConfirmDialog asks before an action that changes or removes something:
 * the shared Dialog (size sm) with the consequences in words, and a footer
 * of [Cancel] [Confirm] — the destructive button filled red only here.
 * Built on the native <dialog> (focus trap, Escape = Cancel, focus returns
 * to the opener). Never a bare "Are you sure?". */
export function ConfirmDialog({
  open,
  title,
  description,
  confirmLabel = 'Confirm',
  danger = false,
  busy = false,
  confirmText,
  confirmDisabled = false,
  children,
  onConfirm,
  onCancel,
}: {
  open: boolean;
  title: string;
  /** Plain-language consequences: what changes, what does not, whether it
   * can be undone. Never a bare "Are you sure?". */
  description: string;
  confirmLabel?: string;
  danger?: boolean;
  busy?: boolean;
  /** When set, the person must type this exact text before the confirm
   * button enables — for actions that destroy history or data. */
  confirmText?: string;
  /** Keeps the confirm button disabled while the caller's own fields (a
   * required reason, say) are not filled in yet. */
  confirmDisabled?: boolean;
  children?: ReactNode;
  onConfirm: () => void;
  onCancel: () => void;
}) {
  const [typed, setTyped] = useState('');
  useEffect(() => {
    if (!open) setTyped('');
  }, [open]);

  const confirmBlocked = confirmDisabled || (Boolean(confirmText) && typed.trim() !== confirmText);

  return (
    <Dialog
      open={open}
      size="sm"
      title={title}
      onClose={onCancel}
      busy={busy}
      hideClose
      className="confirm-dialog"
      footer={
        <>
          <button className="button button--secondary" type="button" onClick={onCancel} disabled={busy}>
            Cancel
          </button>
          <button
            className={`button${danger ? ' button--danger' : ''}`}
            type="button"
            onClick={onConfirm}
            disabled={busy || confirmBlocked}
            // A dangerous confirm never takes the focus: Enter must not perform it.
            autoFocus={!confirmText && !danger}
          >
            {busy ? 'Working' : confirmLabel}
          </button>
        </>
      }
    >
      <p className="dialog__text">{description}</p>
      {children}
      {confirmText ? (
        <Field label={<>Type <code>{confirmText}</code> to confirm</>}>
          <Input
            value={typed}
            onChange={(event) => setTyped(event.target.value)}
            autoComplete="off"
            spellCheck={false}
            disabled={busy}
            autoFocus
          />
        </Field>
      ) : null}
    </Dialog>
  );
}

export { Field } from './primitives/Field';

export function PasswordInput({
  value,
  onChange,
  autoComplete,
  placeholder,
  required = true,
  minLength,
  id,
  name,
  disabled,
  'aria-describedby': ariaDescribedBy,
  'aria-invalid': ariaInvalid,
}: {
  value: string;
  onChange: (e: React.ChangeEvent<HTMLInputElement>) => void;
  autoComplete?: string;
  placeholder?: string;
  required?: boolean;
  minLength?: number;
  id?: string;
  name?: string;
  disabled?: boolean;
  /** Inside a `Field` the Input underneath takes the label, description and invalid state from it. */
  'aria-describedby'?: string;
  'aria-invalid'?: boolean;
}) {
  const [show, setShow] = useState(false);

  return (
    <div className="password-input-wrapper">
      <Input
        type={show ? 'text' : 'password'}
        value={value}
        onChange={onChange}
        autoComplete={autoComplete}
        placeholder={placeholder}
        required={required}
        minLength={minLength}
        id={id}
        name={name}
        disabled={disabled}
        aria-describedby={ariaDescribedBy}
        aria-invalid={ariaInvalid}
      />
      <button
        type="button"
        className="password-toggle-btn"
        onClick={() => setShow((prev) => !prev)}
        aria-label={show ? 'Hide characters' : 'Reveal characters'}
        title={show ? 'Hide password' : 'Show password'}
        aria-pressed={show}
      >
        {show ? 'Hide' : 'Show'}
      </button>
    </div>
  );
}

export { FilterChips } from './primitives/Tabs';
